import { FaSyringe, FaPills, FaTint, FaWind } from "react-icons/fa";
import { MdLocalHospital } from "react-icons/md";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";

type MedicationAdministrationType = {
	name: string;
	total: number;
};

type Props = {
	totalMedicationAdministration: MedicationAdministrationType[];
};

// Função para determinar o ícone baseado no nome da via de administração
const getAdministrationIcon = (administrationName: string) => {
	const name = administrationName.toLowerCase();

	if (name.includes("intramuscular")) {
		return <FaSyringe className="text-primary" />;
	}
	if (name.includes("endovenosa") || name.includes("intravenosa")) {
		return <FaTint className="text-info" />;
	}
	if (name.includes("oral")) {
		return <FaPills className="text-success" />;
	}
	if (name.includes("inalação") || name.includes("nebulização")) {
		return <FaWind className="text-warning" />;
	}
	if (name.includes("subcutânea") || name.includes("sc")) {
		return <MdLocalHospital className="text-secondary" />;
	}

	return <FaSyringe className="text-muted" />;
};

// Função para formatar o nome da via de administração
const formatAdministrationName = (name: string) => {
	return name
		.replace("ADMINISTRAÇÃO DE MEDICAMENTOS POR VIA ", "")
		.replace("ADMINISTRAÇÃO DE MEDICAMENTOS POR ", "")
		.replace("(SC)", "")
		.trim();
};

export function MedicationAdministration({ totalMedicationAdministration }: Props) {
	// Calcular total geral
	const totalAdministrations = totalMedicationAdministration.reduce(
		(sum, item) => sum + item.total,
		0
	);

	// Ordenar por quantidade (maior para menor)
	const sortedAdministrations = [...totalMedicationAdministration].sort(
		(a, b) => b.total - a.total
	);

	return (
		<Card className="border-0 shadow-sm">
			<Card.Header className="bg-white border-bottom py-3">
				<div className="d-flex align-items-center">
					<FaSyringe className="text-muted me-3" size={20} />
					<h6 className="mb-0 text-dark">Administração de Medicamentos</h6>
				</div>
			</Card.Header>

			<Card.Body className="p-0">
				<div className="overflow-y-scroll" style={{ maxHeight: "500px" }}>
					<Table responsive hover className="mb-0">
						<thead className="bg-light">
							<tr>
								<th className="ps-3 py-3 border-0">Via de Administração</th>
								<th className="text-center py-3 border-0">Quantidade</th>
								<th className="text-end pe-3 py-3 border-0">Percentual</th>
							</tr>
						</thead>
						<tbody>
							{sortedAdministrations.map((administration, index) => {
								const percentage = (
									(administration.total / totalAdministrations) *
									100
								).toFixed(1);

								return (
									<tr key={index} className="border-0">
										<td className="ps-3 py-3">
											<div className="d-flex align-items-center">
												<div className="me-3">
													{getAdministrationIcon(administration.name)}
												</div>
												<div>
													<div
														className="fw-medium text-dark"
														style={{ fontSize: "0.9rem" }}
													>
														{formatAdministrationName(
															administration.name
														)}
													</div>
													<small className="text-muted">
														{administration.name}
													</small>
												</div>
											</div>
										</td>
										<td className="text-center py-3">
											<div className="fw-bold text-dark">
												{administration.total.toLocaleString()}
											</div>
										</td>
										<td className="text-end pe-3 py-3">
											<Badge bg="light" text="dark" className="px-3 py-2">
												{percentage}%
											</Badge>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			</Card.Body>

			<Card.Footer className="bg-light border-top py-3">
				<div className="d-flex justify-content-between align-items-center">
					<div className="text-muted">
						<small>Total de administrações</small>
					</div>
					<div className="fw-bold text-dark">{totalAdministrations.toLocaleString()}</div>
				</div>
			</Card.Footer>
		</Card>
	);
}
